/**
 * Keyboard input → an intent vector.
 *
 * Adapted from the Pizza Legends `DirectionInput` model (video 4), with one
 * change: that class mutated the hero directly, whereas this only *reports*
 * what is held. The server decides what that means for position.
 *
 * Held state (rather than keypress events) is right for movement — you want
 * "left is down right now," not "left was pressed once." Discrete actions
 * (fire, dash) will need the opposite, latched model later.
 */

const KEY_MAP = {
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
};

const EMPTY = Object.freeze({
  up: false, down: false, left: false, right: false,
});

/**
 * Is the event aimed at somewhere the user is typing?
 *
 * WITHOUT THIS, CHAT IS IMPOSSIBLE. Listeners are on `window`, and W/A/S/D are
 * movement keys that call `preventDefault()` — so typing "was" into the chat
 * box would walk the character *and* swallow all three letters, with no error
 * to explain why the input appeared broken.
 */
const isTyping = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable === true;
};

export default class Input {
  constructor() {
    this.held = { ...EMPTY };
    // Belt and braces with `isTyping` above. The flag is the explicit contract
    // — the game says when it wants keys — and the target check catches
    // anything that forgets to set it, including elements the game does not
    // know about.
    this.enabled = true;

    this.onKeyDown = (event) => {
      const direction = KEY_MAP[event.code];
      if (!direction) return;
      if (!this.enabled || isTyping(event.target)) return;
      // Stop arrow keys scrolling the page while playing.
      event.preventDefault();
      this.held[direction] = true;
    };

    this.onKeyUp = (event) => {
      const direction = KEY_MAP[event.code];
      if (!direction) return;
      // ALWAYS clears, whatever the target and whether or not input is enabled.
      // A released key must never stay stuck down — releasing W just after the
      // chat box took focus would otherwise walk the player into a wall until
      // they pressed and released it again over the canvas.
      //
      // preventDefault is the conditional part: it has no business firing while
      // someone is typing.
      if (!isTyping(event.target)) event.preventDefault();
      this.held[direction] = false;
    };

    this.onBlur = () => {
      // Without this, tabbing away mid-move leaves the key stuck down and the
      // player walks into a wall forever. The keyup never arrives because the
      // window no longer has focus.
      this.held = { ...EMPTY };
    };
  }

  start() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  stop() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.held = { ...EMPTY };
  }

  /**
   * Hand keyboard control to the game, or release it to the page.
   *
   * Disabling also CLEARS what is held — otherwise walking left and hitting
   * Enter to chat leaves `left` stuck true, and the player keeps moving while
   * they type with no key to release.
   */
  setEnabled(value) {
    this.enabled = Boolean(value);
    if (!this.enabled) this.held = { ...EMPTY };
  }

  /** Current intent. Returns a copy so callers cannot mutate internal state. */
  snapshot() {
    return { ...this.held };
  }

  /** True when any direction is held — used to skip redundant emits. */
  get isIdle() {
    const {
      up, down, left, right,
    } = this.held;
    return !up && !down && !left && !right;
  }
}
